/**
 * ARMORE cart mutation UI guard
 * Production module used by theme.js CartDrawer for lock/unlock during AJAX qty/remove.
 *
 * Lock ownership: snapshots pre-existing disabled / is-disabled state and restores it.
 * Never enables controls that were already disabled (max qty, stock, other owners).
 */
(function (global) {
  'use strict';

  var ATTR_LOCK = 'data-cart-mutation-lock';
  var ATTR_PRE_DISABLED = 'data-cart-mutation-pre-disabled';
  var ATTR_PRE_CLASS = 'data-cart-mutation-pre-class';
  var ATTR_OWNED = 'data-cart-mutation-owned';

  function ArmoreCartMutationGuard(options) {
    this.document = options.document || global.document;
    this.itemSelector = options.itemSelector || '[data-item]';
    this.removeSelector = options.removeSelector || '[data-item-remove]';
    this.quantityPlusSelector = options.quantityPlusSelector || '[data-quantity-plus]';
    this.quantityFieldSelector = options.quantityFieldSelector || '[data-quantity-field]';
    this.disabledClass = options.disabledClass || 'is-disabled';
    this.loadingClass = options.loadingClass || 'is-loading';
    this.lockCount = 0;
  }

  ArmoreCartMutationGuard.prototype._controlsForItem = function (item) {
    var list = [];
    var input = item.querySelector(this.quantityFieldSelector) || item.querySelector('input');
    if (input) list.push(input);
    item.querySelectorAll('button').forEach(function (btn) {
      list.push(btn);
    });
    var self = this;
    item.querySelectorAll(this.removeSelector).forEach(function (el) {
      if (list.indexOf(el) === -1) list.push(el);
    });
    return list;
  };

  ArmoreCartMutationGuard.prototype._snapshotControl = function (el) {
    if (el.hasAttribute(ATTR_PRE_DISABLED)) return;
    el.setAttribute(ATTR_PRE_DISABLED, el.disabled ? '1' : '0');
    el.setAttribute(ATTR_PRE_CLASS, el.classList.contains(this.disabledClass) ? '1' : '0');
  };

  ArmoreCartMutationGuard.prototype._applyLockToControl = function (el) {
    this._snapshotControl(el);
    if (!el.disabled) {
      el.disabled = true;
      el.setAttribute(ATTR_OWNED, '1');
    }
    if (!el.classList.contains(this.disabledClass)) {
      el.classList.add(this.disabledClass);
      el.setAttribute(ATTR_OWNED + '-class', '1');
    }
  };

  ArmoreCartMutationGuard.prototype._restoreControl = function (el) {
    if (!el.hasAttribute(ATTR_PRE_DISABLED)) return;
    var preDisabled = el.getAttribute(ATTR_PRE_DISABLED) === '1';
    var preClass = el.getAttribute(ATTR_PRE_CLASS) === '1';

    if (el.hasAttribute(ATTR_OWNED)) {
      el.disabled = preDisabled;
      el.removeAttribute(ATTR_OWNED);
    } else {
      // Never owned — leave native disabled alone (still pre-disabled).
      el.disabled = preDisabled || el.disabled;
    }

    if (el.hasAttribute(ATTR_OWNED + '-class')) {
      if (!preClass) el.classList.remove(this.disabledClass);
      el.removeAttribute(ATTR_OWNED + '-class');
    } else if (preClass) {
      el.classList.add(this.disabledClass);
    }

    el.removeAttribute(ATTR_PRE_DISABLED);
    el.removeAttribute(ATTR_PRE_CLASS);
  };

  ArmoreCartMutationGuard.prototype.lock = function (holder) {
    this.lockCount += 1;
    var items = this.document.querySelectorAll(this.itemSelector);
    var self = this;
    items.forEach(function (item) {
      item.setAttribute(ATTR_LOCK, '1');
      if (!item.classList.contains(self.disabledClass)) {
        item.classList.add(self.disabledClass);
        item.setAttribute(ATTR_OWNED + '-item-class', '1');
      }
      if (holder && holder.closest && holder.closest(self.itemSelector) === item) {
        item.classList.add(self.loadingClass);
      }
      self._controlsForItem(item).forEach(function (el) {
        self._applyLockToControl(el);
      });
    });
  };

  ArmoreCartMutationGuard.prototype.unlock = function () {
    this.lockCount = Math.max(0, this.lockCount - 1);
    if (this.lockCount > 0) return { unlocked: false, reason: 'nested_lock' };

    var items = this.document.querySelectorAll(this.itemSelector);
    var self = this;
    var unlocked = 0;
    items.forEach(function (item) {
      if (!item.hasAttribute(ATTR_LOCK)) return;
      item.removeAttribute(ATTR_LOCK);
      if (item.hasAttribute(ATTR_OWNED + '-item-class')) {
        item.classList.remove(self.disabledClass);
        item.removeAttribute(ATTR_OWNED + '-item-class');
      }
      item.classList.remove(self.loadingClass);
      self._controlsForItem(item).forEach(function (el) {
        self._restoreControl(el);
      });
      unlocked += 1;
    });
    this.refreshQuantityMaxState();
    return { unlocked: true, count: unlocked };
  };

  /** Force unlock after catastrophic failure (resets nested lock counter). */
  ArmoreCartMutationGuard.prototype.forceUnlock = function () {
    this.lockCount = 1;
    return this.unlock();
  };

  /**
   * After theme build() replaced cart line DOM: drop ownership bookkeeping.
   * Fresh nodes have no lock attrs; only reset the counter + max-qty UI.
   * Prefer this over forceUnlock() for successful rebuilds — forceUnlock is for failures.
   */
  ArmoreCartMutationGuard.prototype.clearAfterDomRebuild = function () {
    this.lockCount = 0;
    this.refreshQuantityMaxState();
    return { cleared: true };
  };

  /**
   * Re-apply lock attributes to the current cart DOM without changing lockCount.
   * Used when CRO/theme replaces line-item HTML while a mutation is still in flight:
   * ownership stays with the in-flight operation; only the new nodes need the lock UI.
   */
  ArmoreCartMutationGuard.prototype.reapplyDomLocks = function (holder) {
    if (this.lockCount <= 0) {
      return { applied: false, reason: 'not_locked' };
    }
    var items = this.document.querySelectorAll(this.itemSelector);
    var self = this;
    var count = 0;
    items.forEach(function (item) {
      item.setAttribute(ATTR_LOCK, '1');
      if (!item.classList.contains(self.disabledClass)) {
        item.classList.add(self.disabledClass);
        item.setAttribute(ATTR_OWNED + '-item-class', '1');
      }
      if (holder && holder.closest && holder.closest(self.itemSelector) === item) {
        item.classList.add(self.loadingClass);
      }
      self._controlsForItem(item).forEach(function (el) {
        self._applyLockToControl(el);
      });
      count += 1;
    });
    return { applied: true, count: count, lockCount: this.lockCount };
  };

  /**
   * Re-apply inventory/max quantity disable after unlock.
   * Sets both native disabled and disabledClass when at max.
   * When below max, clears max-driven disable unless marked data-external-disabled.
   */
  ArmoreCartMutationGuard.prototype.refreshQuantityMaxState = function () {
    var self = this;
    this.document.querySelectorAll(this.itemSelector).forEach(function (item) {
      var field = item.querySelector(self.quantityFieldSelector);
      var plus = item.querySelector(self.quantityPlusSelector);
      if (!field || !plus) return;
      if (item.hasAttribute(ATTR_LOCK)) return;
      var maxAttr = field.getAttribute('max');
      var maxValue = maxAttr && Number(maxAttr) > 0 ? Number(maxAttr) : null;
      var value = Number(field.value || 0);
      var atMax = maxValue !== null && value >= maxValue;
      if (atMax) {
        plus.disabled = true;
        plus.classList.add(self.disabledClass);
        return;
      }
      if (plus.hasAttribute('data-external-disabled')) {
        plus.disabled = true;
        plus.classList.add(self.disabledClass);
        return;
      }
      // Below max and not externally owned: ensure plus is usable.
      plus.disabled = false;
      plus.classList.remove(self.disabledClass);
    });
  };

  /**
   * Wrap a Promise-returning cart rebuild. On failure force-unlocks mutation locks.
   * neverReject: when true, resolves null instead of rejecting (fire-and-forget callers).
   */
  ArmoreCartMutationGuard.prototype.guardRebuild = function (rebuildPromise, opts) {
    var self = this;
    var neverReject = !!(opts && opts.neverReject);
    return Promise.resolve(rebuildPromise).catch(function (error) {
      self.forceUnlock();
      if (neverReject) {
        if (typeof console !== 'undefined' && console.log) console.log(error);
        return null;
      }
      throw error;
    });
  };

  global.ArmoreCartMutationGuard = ArmoreCartMutationGuard;
})(typeof window !== 'undefined' ? window : globalThis);
